import { FunctionComponent } from "react";

import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";

import { BrandsIcon } from "@/components/BrandsIcon/BrandsIcon";
import { classNames } from "@/utils/classNames";

import { LinkedinTier } from "./senderSelector.types";

const TIER_CONFIG: Record<LinkedinTier, { label: string; className: string }> = {
	salesnav: { label: "Sales Nav", className: "bg-blue-action-200 text-blue-action" },
	premium: { label: "Premium", className: "bg-blue-action-200 text-brand-light" },
	connected: {
		label: "LinkedIn",
		className: "bg-brand-white text-text-meta border border-border-light",
	},
	none: {
		label: "Not connected",
		className: "bg-brand-white text-text-meta/70 border border-border-light",
	},
};

/**
 * Compact LinkedIn tier badge for a sender row: Sales Nav / Premium / connected / not connected.
 * `active` is whether the user's LinkedIn sender is on for this campaign: an account that is
 * connected but switched off is shown greyed, so the badge never reads as covering LinkedIn steps
 * it will not send (PREC-4324).
 */
export const LinkedinTierBadge: FunctionComponent<{ tier: LinkedinTier; active?: boolean }> = ({
	tier,
	active = true,
}) => {
	const { label, className } = TIER_CONFIG[tier];
	const off = tier !== "none" && !active;

	return (
		<span
			className={classNames(
				"inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1 text-xs font-semibold",
				off ? TIER_CONFIG.none.className : className
			)}
			data-test="linkedin-tier-badge"
			data-active={off ? "false" : "true"}
			title={off ? "LinkedIn is off for this campaign" : undefined}
		>
			<BrandsIcon className="h-3 w-3" icon={faLinkedinIn as IconDefinition} />
			{label}
		</span>
	);
};
